import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface Country {
  code: string;
  name: string;
  dial: string;
  flag: string;
  digits: [number, number]; // [min, max] local digits
}

export const PHONE_COUNTRIES: Country[] = [
  { code: 'SA', name: 'السعودية',  dial: '966', flag: '🇸🇦', digits: [9, 9] },
  { code: 'AE', name: 'الإمارات',  dial: '971', flag: '🇦🇪', digits: [9, 9] },
  { code: 'SD', name: 'السودان',   dial: '249', flag: '🇸🇩', digits: [9, 9] },
  { code: 'EG', name: 'مصر',       dial: '20',  flag: '🇪🇬', digits: [10, 10] },
  { code: 'JO', name: 'الأردن',    dial: '962', flag: '🇯🇴', digits: [9, 9] },
  { code: 'KW', name: 'الكويت',    dial: '965', flag: '🇰🇼', digits: [8, 8] },
  { code: 'BH', name: 'البحرين',   dial: '973', flag: '🇧🇭', digits: [8, 8] },
  { code: 'QA', name: 'قطر',       dial: '974', flag: '🇶🇦', digits: [8, 8] },
  { code: 'OM', name: 'عُمان',     dial: '968', flag: '🇴🇲', digits: [8, 8] },
  { code: 'IQ', name: 'العراق',    dial: '964', flag: '🇮🇶', digits: [10, 10] },
  { code: 'YE', name: 'اليمن',     dial: '967', flag: '🇾🇪', digits: [9, 9] },
  { code: 'LB', name: 'لبنان',     dial: '961', flag: '🇱🇧', digits: [7, 8] },
  { code: 'SY', name: 'سوريا',     dial: '963', flag: '🇸🇾', digits: [9, 9] },
  { code: 'LY', name: 'ليبيا',     dial: '218', flag: '🇱🇾', digits: [9, 10] },
  { code: 'MA', name: 'المغرب',    dial: '212', flag: '🇲🇦', digits: [9, 9] },
  { code: 'TN', name: 'تونس',      dial: '216', flag: '🇹🇳', digits: [8, 8] },
  { code: 'DZ', name: 'الجزائر',   dial: '213', flag: '🇩🇿', digits: [9, 9] },
  { code: 'TR', name: 'تركيا',     dial: '90',  flag: '🇹🇷', digits: [10, 10] },
  { code: 'PK', name: 'باكستان',   dial: '92',  flag: '🇵🇰', digits: [10, 10] },
  { code: 'IN', name: 'الهند',     dial: '91',  flag: '🇮🇳', digits: [10, 10] },
  { code: 'GB', name: 'بريطانيا',  dial: '44',  flag: '🇬🇧', digits: [10, 10] },
  { code: 'US', name: 'أمريكا',    dial: '1',   flag: '🇺🇸', digits: [10, 10] },
];

/**
 * Validate a recipient phone typed as a *local* number (no dial code) against
 * the selected ISO country code. Lightweight digit-count check — tolerant of a
 * leading trunk "0" or an accidentally-included dial code. The backend
 * (libphonenumber-js) stays the strict gate; this just catches obvious typos
 * before submit so the user gets instant feedback.
 */
export function isValidLocalPhone(value: string, countryCode: string): boolean {
  const country = PHONE_COUNTRIES.find(c => c.code === countryCode);
  if (!country) return false;
  let digits = value.replace(/\D/g, '').replace(/^0+/, '');
  if (digits.startsWith(country.dial)) {
    digits = digits.slice(country.dial.length).replace(/^0+/, '');
  }
  return digits.length >= country.digits[0] && digits.length <= country.digits[1];
}

// Validate a full E.164-style number (+diallocal)
export function validatePhone(value: string): boolean {
  if (!value || !value.startsWith('+')) return false;
  const stripped = value.slice(1);
  const sorted = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  const country = sorted.find(c => stripped.startsWith(c.dial));
  if (!country) {
    const digits = stripped.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  }
  const local = stripped.slice(country.dial.length).replace(/\D/g, '');
  return local.length >= country.digits[0] && local.length <= country.digits[1];
}

function detectCountry(value: string): { country: Country; local: string } {
  const fallback = PHONE_COUNTRIES[0];
  if (!value) return { country: fallback, local: '' };
  const stripped = value.startsWith('+') ? value.slice(1) : value;
  const sorted = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (stripped.startsWith(c.dial)) {
      return { country: c, local: stripped.slice(c.dial.length) };
    }
  }
  return { country: fallback, local: stripped };
}

export interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  disabled?: boolean;
  className?: string;
  /** Pass error string to show red border + message (e.g. from form validation) */
  error?: string;
  /**
   * Lock the input to a specific country (ISO code, e.g. "SA"). When set, the
   * country dropdown is replaced by a static dial-code prefix — use this when
   * the country is chosen by a separate control (e.g. a shipping-destination
   * selector). Omit for the default self-contained picker.
   */
  country?: string;
}

export function PhoneInput({
  value = '',
  onChange,
  onBlur,
  disabled,
  className,
  error,
  country: countryProp,
}: PhoneInputProps) {
  const lockedCountry =
    (countryProp && PHONE_COUNTRIES.find(c => c.code === countryProp)) || null;

  const { country: initCountry, local: initLocal } = detectCountry(value);
  const [country, setCountry] = useState<Country>(initCountry);
  const [local, setLocal] = useState(initLocal);

  // When locked, the parent owns the country — otherwise use internal state.
  const activeCountry = lockedCountry ?? country;

  // Sync internal state when the external value changes (e.g. form reset)
  useEffect(() => {
    const { country: c, local: l } = detectCountry(value);
    setCountry(c);
    setLocal(l);
  }, [value]);

  // When the parent-controlled country changes, re-emit the number with the
  // new dial code so the stored value stays consistent with the selection.
  useEffect(() => {
    if (lockedCountry && local && !value.startsWith(`+${lockedCountry.dial}`)) {
      onChange?.(`+${lockedCountry.dial}${local}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedCountry?.code]);

  const emit = (dial: string, digits: string) => {
    onChange?.(digits ? `+${dial}${digits}` : '');
  };

  const handleCountryChange = (code: string) => {
    const c = PHONE_COUNTRIES.find(x => x.code === code) ?? PHONE_COUNTRIES[0];
    // Trim the number to the new country's max length when switching.
    const capped = local.slice(0, c.digits[1]);
    setCountry(c);
    setLocal(capped);
    emit(c.dial, capped);
  };

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Strip non-digits and cap at the selected country's max local length.
    const digits = e.target.value
      .replace(/\D/g, '')
      .slice(0, activeCountry.digits[1]);
    setLocal(digits);
    emit(activeCountry.dial, digits);
  };

  return (
    <div className={cn('space-y-1', className)}>
      <div
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background overflow-hidden',
          'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
          error && 'border-destructive focus-within:ring-destructive',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        {lockedCountry ? (
          /* Static dial-code prefix — the country is owned by a parent control */
          <div
            className="flex w-[96px] shrink-0 items-center gap-1 border-r bg-muted/40 px-2 text-sm"
            dir="ltr"
          >
            <span>{lockedCountry.flag}</span>
            <span className="text-muted-foreground text-xs">+{lockedCountry.dial}</span>
          </div>
        ) : (
          /* Country selector */
          <Select value={country.code} onValueChange={handleCountryChange} disabled={disabled}>
            <SelectTrigger
              className="w-[96px] shrink-0 border-0 border-r rounded-none focus:ring-0 focus:ring-offset-0 bg-muted/40 h-full px-2 gap-1"
              dir="ltr"
            >
              <SelectValue>
                <span className="flex items-center gap-1 text-sm" dir="ltr">
                  <span>{country.flag}</span>
                  <span className="text-muted-foreground text-xs">+{country.dial}</span>
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {PHONE_COUNTRIES.map(c => (
                <SelectItem key={c.code} value={c.code}>
                  <span className="flex items-center gap-2" dir="ltr">
                    <span>{c.flag}</span>
                    <span className="text-xs text-muted-foreground">+{c.dial}</span>
                    <span>{c.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Local number input */}
        <input
          type="tel"
          dir="ltr"
          value={local}
          onChange={handleLocalChange}
          onBlur={onBlur}
          disabled={disabled}
          placeholder="5XXXXXXXX"
          className={cn(
            'flex-1 min-w-0 h-full px-3 text-sm bg-transparent outline-none',
            'placeholder:text-muted-foreground',
            disabled && 'pointer-events-none',
          )}
        />
      </div>

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
}
